<template>
  <div v-if="isPanelVisible" class="ai-briefing" :class="{ 'is-minimized': minimized }">
    <div class="ai-briefing-header">
      <button type="button" class="ai-briefing-title-wrap" @click="minimized = !minimized">
        <span class="ai-briefing-icon">
          <i class="bi bi-stars"></i>
        </span>
        <h3 class="ai-briefing-title">Briefing IA · pré-consulta</h3>
        <span v-if="minimized && cards.length" class="ai-briefing-count">{{ cards.length }}</span>
      </button>

      <div class="ai-briefing-actions">
        <button type="button" class="ai-briefing-ask-more" @click="$emit('open-patient-chat')">
          Perguntar mais
        </button>

        <button
          type="button"
          class="ai-briefing-close-btn"
          :title="minimized ? 'Expandir briefing' : 'Minimizar briefing'"
          @click="minimized = !minimized"
        >
          <i :class="minimized ? 'bi bi-chevron-down' : 'bi bi-chevron-up'"></i>
        </button>

        <div class="dropdown">
          <button
            type="button"
            class="ai-briefing-menu-btn"
            data-bs-toggle="dropdown"
            aria-expanded="false"
            title="Opções do briefing"
          >
            <i class="bi bi-three-dots-vertical"></i>
          </button>
          <ul class="dropdown-menu dropdown-menu-end">
            <li>
              <button type="button" class="dropdown-item" @click="minimized = !minimized">
                {{ minimized ? 'Expandir' : 'Minimizar' }}
              </button>
            </li>
            <li>
              <button type="button" class="dropdown-item" @click="dismissBriefing">
                Fechar neste atendimento
              </button>
            </li>
            <li>
              <button type="button" class="dropdown-item text-danger" @click="disableBriefing">
                Desativar briefing
              </button>
            </li>
          </ul>
        </div>

        <button
          type="button"
          class="ai-briefing-close-btn"
          title="Fechar neste atendimento"
          @click="dismissBriefing"
        >
          <i class="bi bi-x-lg"></i>
        </button>
      </div>
    </div>

    <template v-if="!minimized">
      <div v-if="loading" class="ai-briefing-grid">
        <div v-for="index in 3" :key="index" class="ai-briefing-card ai-briefing-card--skeleton">
          <div class="skeleton-icon"></div>
          <div class="skeleton-title"></div>
          <div class="skeleton-text"></div>
          <div class="skeleton-text skeleton-text--short"></div>
        </div>
      </div>

      <div v-else-if="error" class="ai-briefing-empty">
        <p>{{ error }}</p>
        <button type="button" class="btn btn-sm btn-light-primary" @click="loadBriefing">
          Tentar novamente
        </button>
      </div>

      <div v-else-if="cards.length > 0" class="ai-briefing-grid">
        <article
          v-for="(card, index) in cards"
          :key="`${card.title}-${index}`"
          class="ai-briefing-card"
        >
          <div class="ai-briefing-card-head">
            <span class="ai-briefing-status-icon" :class="`ai-briefing-status-icon--${card.status}`">
              <i :class="statusIcon(card.status)"></i>
            </span>
            <h4 class="ai-briefing-card-title" :class="`ai-briefing-card-title--${card.status}`">
              {{ card.title }}
            </h4>
          </div>
          <p class="ai-briefing-card-text">{{ card.text }}</p>
        </article>
      </div>

      <div v-else class="ai-briefing-empty">
        <p class="mb-2">Sem dados suficientes para gerar o briefing.</p>
        <button type="button" class="btn btn-sm btn-light-primary" @click="$emit('open-patient-chat')">
          Perguntar sobre o paciente
        </button>
      </div>
    </template>
  </div>
</template>

<script>
import http from '@/helpers/http';
import {
  AI_ASSISTANT_PREFS_CHANGED,
  loadAiAssistantPrefs,
  resolveEffectiveAiFeatures,
  saveAiAssistantPrefs,
} from '@/helpers/aiAssistantPrefs';

export default {
  name: 'AiBriefingPanel',

  props: {
    schedule: {
      type: Object,
      default: null,
    },
    recordTypes: {
      type: Array,
      default: () => [],
    },
    professionalId: {
      type: [String, Number],
      default: null,
    },
  },

  emits: ['open-patient-chat'],

  data() {
    return {
      cards: [],
      loading: false,
      error: null,
      loaded: false,
      skipped: false,
      profPrefs: loadAiAssistantPrefs(this.professionalId),
      dismissedScheduleId: null,
      minimized: false,
    };
  },

  computed: {
    features() {
      return resolveEffectiveAiFeatures(this.recordTypes, this.profPrefs);
    },

    isPanelVisible() {
      if (!this.canLoadBriefing || this.skipped) {
        return false;
      }

      return this.loading || this.error || this.loaded;
    },

    canLoadBriefing() {
      return this.features.briefing
        && this.schedule?.id_prof
        && this.schedule?.id_patient
        && this.dismissedScheduleId !== this.schedule.id_sched;
    },
  },

  watch: {
    professionalId(id) {
      this.profPrefs = loadAiAssistantPrefs(id);
    },

    canLoadBriefing: {
      immediate: true,
      handler(canLoad) {
        if (canLoad) {
          this.loadBriefing();
        }
      },
    },

    'schedule.id_sched'(scheduleId) {
      this.minimized = false;
      if (scheduleId !== this.dismissedScheduleId && this.canLoadBriefing) {
        this.loadBriefing();
      }
    },
  },

  mounted() {
    window.addEventListener(AI_ASSISTANT_PREFS_CHANGED, this.onPrefsChanged);
  },

  beforeUnmount() {
    window.removeEventListener(AI_ASSISTANT_PREFS_CHANGED, this.onPrefsChanged);
  },

  methods: {
    onPrefsChanged(event) {
      if (String(event.detail?.professionalId ?? '') !== String(this.professionalId ?? '')) {
        return;
      }

      this.profPrefs = loadAiAssistantPrefs(this.professionalId);
    },

    dismissBriefing() {
      this.dismissedScheduleId = this.schedule?.id_sched ?? null;
    },

    disableBriefing() {
      this.profPrefs = {
        ...this.profPrefs,
        briefing_enabled: false,
      };
      saveAiAssistantPrefs(this.professionalId, this.profPrefs);
    },

    loadBriefing() {
      if (!this.canLoadBriefing) {
        return;
      }

      this.loading = true;
      this.error = null;
      this.loaded = false;
      this.skipped = false;

      http.get(
        `/professionals/${this.schedule.id_prof}/patients/${this.schedule.id_patient}/medical-records/ai/briefing`,
        { params: { current_schedule_id: this.schedule.id_sched } },
      )
        .then((response) => {
          this.cards = response.data?.cards ?? [];
          this.skipped = response.data?.skipped ?? false;
        })
        .catch(() => {
          this.error = 'Não foi possível gerar o briefing.';
          this.cards = [];
        })
        .finally(() => {
          this.loading = false;
          this.loaded = true;
        });
    },

    statusIcon(status) {
      const icons = {
        danger: 'bi bi-exclamation-lg',
        warning: 'bi bi-clock-history',
        success: 'bi bi-check-lg',
        info: 'bi bi-info-lg',
      };

      return icons[status] ?? icons.info;
    },
  },
};
</script>

<style scoped>
.ai-briefing {
  background: #f8f9fb;
  border: 1px solid #ebedf3;
  border-radius: 0.85rem;
  padding: 1rem 1.15rem 1.15rem;
  margin-bottom: 1.5rem;
}

.ai-briefing.is-minimized {
  padding-bottom: 0.85rem;
}

.ai-briefing.is-minimized .ai-briefing-header {
  margin-bottom: 0;
}

.ai-briefing-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.ai-briefing-title-wrap {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  min-width: 0;
  border: none;
  background: transparent;
  padding: 0;
  text-align: left;
  cursor: pointer;
}

.ai-briefing-count {
  flex-shrink: 0;
  min-width: 1.35rem;
  height: 1.35rem;
  padding: 0 0.35rem;
  border-radius: 999px;
  background: #e8eef7;
  color: #3f4254;
  font-size: 0.72rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.ai-briefing-actions {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  flex-shrink: 0;
}

.ai-briefing-icon {
  width: 2rem;
  height: 2rem;
  border-radius: 0.5rem;
  background: linear-gradient(135deg, #1e3a5f 0%, #2d4a6f 100%);
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 0.95rem;
}

.ai-briefing-title {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: #181c32;
}

.ai-briefing-ask-more {
  border: none;
  background: transparent;
  color: #780f5a;
  font-size: 0.875rem;
  font-weight: 600;
  padding: 0.15rem 0.35rem;
  white-space: nowrap;
}

.ai-briefing-ask-more:hover {
  text-decoration: underline;
}

.ai-briefing-menu-btn,
.ai-briefing-close-btn {
  border: none;
  background: transparent;
  color: #7e8299;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 0.35rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.ai-briefing-menu-btn:hover,
.ai-briefing-close-btn:hover {
  background: #eef0f4;
  color: #181c32;
}

.ai-briefing-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.85rem;
}

.ai-briefing-card {
  background: #fff;
  border: 1px solid #eef0f4;
  border-radius: 0.75rem;
  padding: 1rem;
  min-height: 120px;
}

.ai-briefing-card-head {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.55rem;
}

.ai-briefing-status-icon {
  width: 1.35rem;
  height: 1.35rem;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  flex-shrink: 0;
}

.ai-briefing-status-icon--danger {
  background: #fff5f8;
  color: #f1416c;
}

.ai-briefing-status-icon--warning {
  background: #fff8dd;
  color: #f6c000;
}

.ai-briefing-status-icon--success {
  background: #e8fff3;
  color: #50cd89;
}

.ai-briefing-status-icon--info {
  background: #f1faff;
  color: #009ef7;
}

.ai-briefing-card-title {
  margin: 0;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
}

.ai-briefing-card-title--danger { color: #f1416c; }
.ai-briefing-card-title--warning { color: #dfa000; }
.ai-briefing-card-title--success { color: #47b881; }
.ai-briefing-card-title--info { color: #009ef7; }

.ai-briefing-card-text {
  margin: 0;
  font-size: 0.84rem;
  line-height: 1.5;
  color: #5e6278;
}

.ai-briefing-empty {
  text-align: center;
  padding: 1.25rem 0.5rem;
  color: #7e8299;
  font-size: 0.875rem;
}

.ai-briefing-card--skeleton .skeleton-icon,
.ai-briefing-card--skeleton .skeleton-title,
.ai-briefing-card--skeleton .skeleton-text {
  background: linear-gradient(90deg, #f1f3f8 25%, #e9edf5 50%, #f1f3f8 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
  border-radius: 0.35rem;
}

.skeleton-icon {
  width: 1.35rem;
  height: 1.35rem;
  border-radius: 50%;
  margin-bottom: 0.75rem;
}

.skeleton-title {
  width: 55%;
  height: 0.75rem;
  margin-bottom: 0.75rem;
}

.skeleton-text {
  width: 100%;
  height: 0.65rem;
  margin-bottom: 0.45rem;
}

.skeleton-text--short {
  width: 80%;
}

@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

@media (max-width: 991px) {
  .ai-briefing-grid {
    grid-template-columns: 1fr;
  }
}

@media (min-width: 992px) and (max-width: 1199px) {
  .ai-briefing-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
